import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CacheProvider } from '@emotion/react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { theme } from '@/theme';
import { isRtl } from '@/i18n/config';
import { cacheForDirection } from '@/i18n/rtlCache';
import { GlobalErrorBoundary, ToastContainer } from '@/components/ui';
import { AppRouter } from '@/router'

/**
 * Main App component
 * Sets up the (direction-aware) theme, emotion RTL cache, error boundary, and
 * router. For Arabic the MUI theme direction flips to RTL and the emotion cache
 * runs stylis-plugin-rtl so physical CSS (margins/padding/alignment) mirrors.
 */
export function App() {
  const { i18n } = useTranslation();
  const direction = isRtl(i18n.language) ? 'rtl' : 'ltr';

  // Derive a direction-aware theme from the base theme.
  const directionalTheme = useMemo(
    () => createTheme(theme, { direction }),
    [direction]
  );

  const emotionCache = useMemo(() => cacheForDirection(direction), [direction]);

  return (
    <CacheProvider value={emotionCache}>
      <ThemeProvider theme={directionalTheme}>
        <CssBaseline />
        <GlobalErrorBoundary>
          <AppRouter />
          <ToastContainer />
        </GlobalErrorBoundary>
      </ThemeProvider>
    </CacheProvider>
  )
}
